/**
 * Spec 217 PR4.b — i18next bootstrap.
 *
 * Init runs once at module load (imported from `main.tsx`). The
 * resource bundles are statically imported so they ship as part of
 * the main chunk — both Spanish + English fit in <2 KB gzip
 * combined and the round-trip avoidance outweighs the bundle hit.
 *
 * Detector chain (priority order):
 *   1. localStorage `cuadrilla-web.locale-v1` — explicit user pick
 *      via Settings → Idioma. This always wins.
 *   2. fallback `'es'` — Cuadrilla's primary user base. We
 *      intentionally skip `navigator.language` so a Spanish-language
 *      product doesn't auto-flip to English on a US-default browser
 *      install — that has been a recurring complaint in similar B2B
 *      apps. Users who prefer English flip explicitly in Settings.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { es } from './es';
import { en } from './en';

export const LOCALE_STORAGE_KEY = 'cuadrilla-web.locale-v1';
export const SUPPORTED_LOCALES = ['es', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

void i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      es: { translation: es },
      en: { translation: en },
    },
    fallbackLng: 'es',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false, // React already escapes output
    },
    detection: {
      // localStorage only — see header for the navigator-skip rationale.
      order: ['localStorage'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

/** Imperative setter used by the Settings → Idioma switcher. Persists
 *  via the detector's localStorage cache so a reload keeps the pick. */
export function setLocale(locale: SupportedLocale): Promise<unknown> {
  return i18next.changeLanguage(locale);
}

/** Reads the current locale, normalised to one of `SUPPORTED_LOCALES`.
 *  Useful for the Settings UI which highlights the active option. */
export function currentLocale(): SupportedLocale {
  const raw = (i18next.language || 'es').toLowerCase().split('-')[0];
  return (SUPPORTED_LOCALES as readonly string[]).includes(raw)
    ? (raw as SupportedLocale)
    : 'es';
}

export default i18next;
